import React from 'react';
import { Button } from '@/components/ui/button';
import { Plus, RotateCcw, Eye, Edit, Copy, Archive, Trash2, Building2 } from 'lucide-react';
import { Link } from 'react-router-dom';

const AdminScholarshipsTab = ({
  showArchived,
  setShowArchived,
  openScholarshipDialog,
  scholarships,
  committees,
  unarchiveScholarship,
  duplicateScholarship,
  archiveScholarship,
  deleteScholarship
}) => {
  return (
    <>
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-4">
          <h2 className="font-heading text-2xl font-bold text-slate-900">Manage Scholarships</h2>
          <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
              className="rounded border-slate-300"
            />
            Show Archived
          </label>
        </div>
        <Button
          onClick={() => openScholarshipDialog()}
          className="bg-greek-blue hover:bg-greek-blue-dark text-white rounded-sm"
          data-testid="create-scholarship-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Create Scholarship
        </Button>
      </div>

      <div className="space-y-4">
        {scholarships.map((scholarship) => {
          const assignedCommittee = committees.find(c => c.id === scholarship.committee_id);
          return (
          <div key={scholarship.id} className={`bg-white rounded-sm border p-6 ${scholarship.is_archived ? 'border-slate-300 bg-slate-50 opacity-75' : 'border-slate-200'}`} data-testid={`scholarship-row-${scholarship.id}`}>
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h3 className={`font-heading text-lg font-semibold ${scholarship.is_archived ? 'text-slate-500' : 'text-slate-900'}`}>{scholarship.name}</h3>
                  {scholarship.is_archived ? (
                    <span className="px-2 py-1 text-xs rounded-sm font-medium bg-slate-200 text-slate-600">
                      Archived
                    </span>
                  ) : (
                    <span className={`px-2 py-1 text-xs rounded-sm font-medium ${scholarship.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                      {scholarship.is_active ? 'Active' : 'Inactive'}
                    </span>
                  )}
                  {assignedCommittee && (
                    <span className="px-2 py-1 text-xs rounded-sm font-medium bg-greek-blue/10 text-greek-blue flex items-center gap-1">
                      <Building2 className="w-3 h-3" />
                      {assignedCommittee.name}
                    </span>
                  )}
                </div>
                <p className="text-slate-600 text-sm mb-2">{scholarship.description}</p>
                <div className="flex gap-4 text-sm text-slate-500 flex-wrap">
                  <span>Amount: <strong className="text-greek-gold">${scholarship.amount?.toLocaleString()}</strong></span>
                  <span>Start: {scholarship.start_date ? new Date(scholarship.start_date).toLocaleDateString() : 'N/A'}</span>
                  <span>Deadline: {new Date(scholarship.deadline).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })}</span>
                  <span>Type: {scholarship.scholarship_type?.replace('_', ' ')}</span>
                </div>
              </div>
              <div className="flex gap-2">
                {scholarship.is_archived ? (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => unarchiveScholarship(scholarship.id)}
                    className="rounded-sm text-emerald-600 border-emerald-300 hover:bg-emerald-50"
                    data-testid={`restore-scholarship-btn-${scholarship.id}`}
                    title="Restore from Archive"
                  >
                    <RotateCcw className="w-4 h-4" />
                  </Button>
                ) : (
                  <>
                    <Link 
                      to={`/application/preview/${scholarship.id}`}
                      target="_blank"
                    >
                      <Button
                        variant="outline"
                        size="sm"
                        className="rounded-sm text-emerald-600 border-emerald-300 hover:bg-emerald-50"
                        data-testid={`preview-scholarship-btn-${scholarship.id}`}
                        title="Preview Application Form"
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                    </Link>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openScholarshipDialog(scholarship)}
                      className="rounded-sm"
                      data-testid={`edit-scholarship-btn-${scholarship.id}`}
                      title="Edit Scholarship"
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => duplicateScholarship(scholarship)}
                      className="rounded-sm text-blue-600 border-blue-300 hover:bg-blue-50"
                      data-testid={`duplicate-scholarship-btn-${scholarship.id}`}
                      title="Duplicate Scholarship"
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => archiveScholarship(scholarship.id)}
                      className="rounded-sm text-amber-600 border-amber-300 hover:bg-amber-50"
                      data-testid={`archive-scholarship-btn-${scholarship.id}`}
                      title="Archive Scholarship"
                    >
                      <Archive className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => deleteScholarship(scholarship.id)}
                      className="rounded-sm text-red-600 border-red-300 hover:bg-red-50"
                      data-testid={`delete-scholarship-btn-${scholarship.id}`}
                      title="Delete Scholarship"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </>
                )}
              </div>
            </div>
          </div>
        )})}
      </div>
    </>
  );
};

export default AdminScholarshipsTab;